import { useIsMutating } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { startSyncKey, useAssetSheetActivity, useAssetSheetProgress, useLastSyncQuery } from '@/hooks/useAssetSheet'
import { notification } from '@/notifications/notification-store'
import { describeSyncCounts } from './asset-sheet-view'

/** How long a requested sync, not seen running, waits for its outcome before the page stops expecting one. */
const OUTCOME_WAIT_MS = 60_000

/**
 * The progress, and a "Sync now" the page is waiting on from the request until the sync records
 * its outcome, both polled fast meanwhile. The outcome is told apart by its audit event, not by a
 * clock, so a sync that fails or finishes between two polls still ends the wait and is reported;
 * one that never records an outcome stops being waited on a minute after it was last seen running.
 * A run seen running is no longer starting, even while its outcome is still on its way; only a
 * read after the request has settled counts, not the request's own optimistic "running".
 *
 * Sync now waits for the last outcome to be read, since an outcome is new only against the one
 * before the request; when that read failed, the outcome is not announced, the summary still shows it.
 */
export function useSyncProgress() {
	// `after`: the outcome before the request, `null` for none, undefined when it could not be read.
	const [request, setRequest] = useState<{ after?: string | null; sawRunning: boolean }>()
	const isWaiting = request !== undefined
	const progress = useAssetSheetProgress(isWaiting)
	const lastQuery = useLastSyncQuery(isWaiting)
	const last = lastQuery.data
	// Recent activity shows the outcome as soon as the page learns of it.
	useAssetSheetActivity(isWaiting)
	const running = progress.data?.sync.running ?? false
	const isRequesting = useIsMutating({ mutationKey: startSyncKey }) > 0

	useEffect(() => {
		if (request && running && !isRequesting && !request.sawRunning) setRequest({ ...request, sawRunning: true })
	}, [request, running, isRequesting])

	useEffect(() => {
		if (!request || !last || request.after === undefined || last.eventId === request.after) return
		setRequest(undefined)
		if (last.failure) notification.error(`Sync failed: ${last.failure}`)
		else if (last.counts) {
			const done = `Sync completed: ${describeSyncCounts(last.counts)}`
			const skipped = last.tabProblems.map((p) => p.tab)
			if (skipped.length === 0) notification.success(done)
			else notification.warning(`${done}. Skipped ${skipped.length > 1 ? 'tabs' : 'tab'} ${skipped.join(', ')}.`)
		}
	}, [request, last])

	useEffect(() => {
		if (!request || running) return
		const id = setTimeout(() => setRequest(undefined), OUTCOME_WAIT_MS)
		return () => clearTimeout(id)
	}, [request, running])

	return {
		progress,
		/** Asked for and not yet seen running. */
		isStarting: isWaiting && !running && !request.sawRunning,
		/** The last outcome has been read, or its read failed. */
		canStart: !lastQuery.isPending,
		start: () => setRequest({ after: lastQuery.isSuccess ? (last?.eventId ?? null) : undefined, sawRunning: false }),
		/** The request itself was refused, so no outcome will come. */
		cancel: () => setRequest(undefined),
	}
}
